'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';

export const ENTHIOSHUB_LOADER_MESSAGES = [
  'Preparing your experience...',
  'Getting things ready...',
  'Almost there...',
  'Loading EnthiosHub...',
  'Welcome to EnthiosHub',
] as const;

export type EnthiosHubLoaderVariant = 'fullscreen' | 'overlay' | 'panel' | 'inline';

export type EnthiosHubLoaderProps = {
  /** Custom status message; when omitted, branded messages rotate automatically. */
  message?: string;
  variant?: EnthiosHubLoaderVariant;
  /** Delay before showing the loader to avoid flashing on fast requests. */
  showDelayMs?: number;
  className?: string;
  /** Rotate default branded messages when no custom message is provided. */
  rotateMessages?: boolean;
  /** Accessible label; defaults to the visible message. */
  ariaLabel?: string;
};

export function EnthiosHubLoader({
  message,
  variant = 'panel',
  showDelayMs = 300,
  className,
  rotateMessages,
  ariaLabel,
}: EnthiosHubLoaderProps) {
  const [visible, setVisible] = useState(showDelayMs <= 0);
  const [messageIndex, setMessageIndex] = useState(0);
  const shouldRotate = rotateMessages ?? !message;

  useEffect(() => {
    if (showDelayMs <= 0) {
      setVisible(true);
      return;
    }

    const timer = window.setTimeout(() => setVisible(true), showDelayMs);
    return () => window.clearTimeout(timer);
  }, [showDelayMs]);

  useEffect(() => {
    if (!shouldRotate || message) return;

    const interval = window.setInterval(() => {
      setMessageIndex((current) => (current + 1) % ENTHIOSHUB_LOADER_MESSAGES.length);
    }, 3200);

    return () => window.clearInterval(interval);
  }, [shouldRotate, message]);

  if (!visible) return null;

  const displayMessage = message ?? ENTHIOSHUB_LOADER_MESSAGES[messageIndex];
  const accessibleLabel = ariaLabel ?? displayMessage;

  return (
    <div
      className={cn('eh-loader', `eh-loader--${variant}`, className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={accessibleLabel}
    >
      <div className="eh-loader__inner">
        <div className="eh-loader__stage">
          <svg className="eh-loader__ring" viewBox="0 0 100 100" aria-hidden="true">
            <circle className="eh-loader__ring-track" cx="50" cy="50" r="44" />
            <circle className="eh-loader__ring-progress" cx="50" cy="50" r="44" />
          </svg>
          <div className="eh-loader__logo-wrap">
            <Image
              src="/logo-symbol.png"
              alt="EnthiosHub"
              fill
              loading="eager"
              className="eh-loader__logo object-contain"
              aria-hidden="true"
            />
          </div>
        </div>
        <p className="eh-loader__message" aria-hidden="true">
          {displayMessage}
        </p>
      </div>
    </div>
  );
}
